import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '../../../../services/translate/translate.service';
import { AppContextService } from '../../../../services/app-context.service';
import { LogService } from '../../../../services/log/log.service';
import { ToasterService } from '../../../../shared-components/custom-toaster/toaster.service';
import { OperationResult } from '../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { CodeList } from '../../../../models/common/code-list';
import { User } from '../../../../models/User/user';
import { CompanyFileIntegrations } from '../../../../models/companies/company-file-integrations';
import { PublicCompany } from '../../../../models/companies/public-company';
import { finalize, forkJoin, Observable } from 'rxjs';
import { RegisterFileForProcessing } from '../../../../models/upload/register-file-for-processing';
import { RegisterFileForProcessingOutput } from '../../../../models/upload/register-file-for-processing-output';
import { UtilitiesService } from '../../../../services/utils/utilities.service';
import { DocumentExternalSource } from '../../../../models/enums/document-external-source';
import { FileUploadContentTypeViewModel } from './models/file-upload-content-type-view-model';
import { FileIntegrationApiService } from '../../../../server-api/file-integration-api.service';
import { CodeListApiService } from '../../../../server-api/code-list-api/code-list-api.service';
import { FileTypesEnum } from '../../../../server-api/code-list-api/code-list-api.enums';

@Component({
	selector: 'app-upload-file',
	templateUrl: './upload-file.component.html',
	styleUrls: ['./upload-file.component.css'],
	standalone: false,
})
export class UploadFileComponent implements OnInit {
	@ViewChild('fileInput') private fileInput: ElementRef;

	public fileName: string;
	private fileContent: string;
	public isPerformingRequest = false;

	public fileUploadContentTypeError: string | null;
	public fileUploadContentType: FileUploadContentTypeViewModel | null;
	public fileUploadContentTypes: FileUploadContentTypeViewModel[] = [];

	public companyFileIntegrationsTypes: { [companyId: string]: string[] } = {};
	public companyWithFileIntegrations: PublicCompany[] = [];
	public loggedUser: User | null = new User();
	public hasSubmissionError = false;

	constructor(
		private translateService: TranslateService,
		private codeListApiService: CodeListApiService,
		private fileIntegrationApiService: FileIntegrationApiService,
		private appContext: AppContextService,
		private logger: LogService,
		private toasterService: ToasterService,
		private utilitiesService: UtilitiesService,
		public router: Router
	) {}

	ngOnInit() {
		this.fileName = this.translateService.translate('upload-file.UploadNoSelectedFile', 'client');
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			this.loadData();
		});
	}

	private loadData(): void {
		let servicesCallsToMake: Array<Observable<OperationResult<CodeList<FileTypesEnum> | CompanyFileIntegrations[]>>> = [];
		servicesCallsToMake.push(this.codeListApiService.getCodeListFileTypes());

		if (this.loggedUser?.IsSystemAdmin) {
			servicesCallsToMake.push(this.fileIntegrationApiService.companyFileIntegration([]));
		} else {
			servicesCallsToMake.push(this.fileIntegrationApiService.companyFileIntegration(this.loggedUser?.Companies?.map((c) => c.Id!) ?? []));
		}

		const getAllRequests = forkJoin(servicesCallsToMake);

		getAllRequests.subscribe((opResults) => {
			let getFileTypesResult = opResults[0] as OperationResult<CodeList<FileTypesEnum>>;
			let companyFileIntegrationResult = opResults[1] as OperationResult<CompanyFileIntegrations[]>;

			if (!getFileTypesResult.IsValid) {
				this.processInvalidResult(getFileTypesResult);
				return;
			}

			if (!companyFileIntegrationResult.IsValid) {
				this.processInvalidResult(companyFileIntegrationResult);
				return;
			}

			if (getFileTypesResult.IsValid && companyFileIntegrationResult.IsValid) {
				let companyFileIntegrationsTypesDict: { [companyId: string]: string[] } = {};

				for (let index = 0; index < companyFileIntegrationResult.Data.length; index++) {
					let companyFileIntegrations: CompanyFileIntegrations = companyFileIntegrationResult.Data[index];

					if (!!companyFileIntegrations && !!companyFileIntegrations?.Company?.Id) {
						companyFileIntegrationsTypesDict[companyFileIntegrations?.Company?.Id] = [];

						for (
							let uploadFileContentTypesCodesIndx = 0;
							uploadFileContentTypesCodesIndx < getFileTypesResult.Data.Codes.length;
							uploadFileContentTypesCodesIndx++
						) {
							const uploadFileContentTypeCode = getFileTypesResult.Data.Codes[uploadFileContentTypesCodesIndx];

							if (!!uploadFileContentTypeCode) {
								if (this.loggedUser?.IsSystemAdmin) {
									if (companyFileIntegrations.CompanyFileSourceFileTypes.findIndex((t) => uploadFileContentTypeCode == t.FileType) >= 0) {
										companyFileIntegrationsTypesDict[companyFileIntegrations?.Company?.Id].push(uploadFileContentTypeCode);
									}
								} else {
									if (
										companyFileIntegrations.CompanyFileSourceFileTypes.filter(
											(t) => t.FileSource == DocumentExternalSource.FILE_UPLOAD
										).findIndex((t) => uploadFileContentTypeCode == t.FileType) >= 0
									) {
										companyFileIntegrationsTypesDict[companyFileIntegrations?.Company?.Id].push(uploadFileContentTypeCode);
									}
								}
							}
						}
					}
				}

				this.companyFileIntegrationsTypes = Object.keys(companyFileIntegrationsTypesDict).reduce(function (filtered, key) {
					if (companyFileIntegrationsTypesDict[key].length > 0) {
						filtered[key] = companyFileIntegrationsTypesDict[key];
					}
					return filtered;
				}, {});

				this.companyWithFileIntegrations = companyFileIntegrationResult.Data.filter(
					(c) => Object.keys(this.companyFileIntegrationsTypes).findIndex((k) => k === c.Company.Id) >= 0
				).map((c) => c.Company);

				let allFileTypes = Object.values(this.companyFileIntegrationsTypes).reduce(function (result, items) {
					for (let index = 0; index < items.length; index++) {
						const element = items[index];
						if (result.indexOf(element) < 0) {
							result.push(element);
						}
					}

					return result;
				}, new Array<string>());

				let distinctFileUploadContentTypes = this.utilitiesService.getDistinctValues(allFileTypes);
				let fileUploadContentTypeViewModels: FileUploadContentTypeViewModel[] = distinctFileUploadContentTypes.map(
					(t) =>
						<FileUploadContentTypeViewModel>{
							Type: t,
							TranslatedType: this.loggedUser?.IsSystemAdmin
								? this.translateService.translate(t, 'list-integrationFileType') + ' (' + t + ')'
								: this.translateService.translate(t, 'list-integrationFileType'),
						}
				);

				this.fileUploadContentTypes = fileUploadContentTypeViewModels;

				if (this.fileUploadContentTypes.length == 1) {
					this.fileUploadContentType = this.fileUploadContentTypes[0];
				}
			}
		});
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>, trackErrors: boolean = false): void {
		let errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});

			if (trackErrors) {
				this.hasSubmissionError = true;
			}
		}
	}

	private formIsValid(): boolean {
		this.fileUploadContentTypeError = !this.fileUploadContentType ? 'REQUIRED' : null;

		let validData = !this.fileUploadContentTypeError;
		return validData;
	}

	public registerFileForProcessing(): void {
		if (this.formIsValid()) {
			const registerFileForProcessing: RegisterFileForProcessing = <RegisterFileForProcessing>{
				FileContent: this.fileContent,
				FileName: this.fileName,
				FileType: this.fileUploadContentType?.Type,
			};

			this.isPerformingRequest = true;

			this.fileIntegrationApiService
				.registerFileForProcessing(registerFileForProcessing)
				.pipe(
					finalize(() => {
						this.isPerformingRequest = false;
					})
				)
				.subscribe({
					next: (operationResult: OperationResult<RegisterFileForProcessingOutput>) => {
						if (operationResult.IsValid) {
							this.hasSubmissionError = false;
							this.router.navigate(['/private/files-integration/search']);
						} else {
							this.processInvalidResult(operationResult, true);
						}
					},
					error: (httpErrorResponse: HttpErrorResponse) => {
						this.logger.error('[UploadFileComponent::receiveUploadedFileRequest] HttpErrorResponse: ', httpErrorResponse);
						this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
						this.hasSubmissionError = true;
					},
				});
		}
	}

	public showNextStep(): boolean {
		return !!this.fileContent && !this.hasSubmissionError;
	}

	public onFileChange(event): void {
		this.hasSubmissionError = false;

		let reader = new FileReader();
		if (event.target.files && event.target.files.length > 0) {
			let file = event.target.files[0];
			reader.readAsDataURL(file);
			reader.onload = () => {
				this.fileName = file.name;
			};
			reader.onloadend = () => {
				if (reader.result !== null && reader.result !== undefined && reader.result.toString() !== null) {
					var base64result = (reader.result as string).split(',')[1];
					this.fileContent = base64result;

					if (!this.fileContent) {
						this.toasterService.showError(this.translateService.translate('upload-file.emptyOrInvalidFileContent', 'client'));
					}
				}
			};
		}
	}

	public onChangeFileUploadContentType(): void {
		if (!this.fileUploadContentType) {
			this.fileUploadContentTypeError = 'FILE_CONTENT_TYPE_REQUIRED';
		} else {
			this.fileUploadContentTypeError = null;
		}
	}

	protected selectFile(): void {
		this.fileInput?.nativeElement?.click();
	}

	public navigateToFilesIntegrationSearch(): void {
		this.router.navigate(['/private/files-integration/search']);
	}
}
